import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, ArrowRight, Trash2, Building2, Award, IdCard } from "lucide-react";
import { toast } from "sonner";

const TRACK_META = {
  acis_school:   { label: "Draft Accreditation Applications", icon: Building2, color: "from-blue-50 to-blue-100", badge: "ACIS", resume: "/dashboard/schools/new" },
  ibthm_app:     { label: "Draft Certification Applications", icon: Award,     color: "from-emerald-50 to-emerald-100", badge: "IBTHM", resume: "/dashboard/applications/new" },
  ibthm_license: { label: "Draft Licensing Applications",     icon: IdCard,    color: "from-violet-50 to-violet-100", badge: "IBTHM", resume: "/dashboard/licenses/new" },
};

export default function Drafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/drafts");
      setDrafts(r.data);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this draft? This cannot be undone.")) return;
    try {
      await api.delete(`/drafts/${id}`);
      toast.success("Draft deleted");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const grouped = drafts.reduce((acc, d) => {
    acc[d.track] = acc[d.track] || [];
    acc[d.track].push(d);
    return acc;
  }, {});

  return (
    <div className="animate-fade-up" data-testid="drafts-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">My drafts</div>
        <h1 className="font-heading text-3xl font-bold text-primary">Saved drafts</h1>
        <p className="text-sm text-slate-500 mt-1">
          Auto-saved every 60 seconds while you work. Pick up any unsubmitted application below.
        </p>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && drafts.length === 0 && (
        <Card className="p-10 text-center border-slate-200" data-testid="drafts-empty">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm text-slate-500">
            No drafts yet. Start a new application and your progress will be saved here automatically.
          </div>
        </Card>
      )}

      <div className="space-y-8">
        {Object.entries(TRACK_META).map(([track, meta]) => {
          const items = grouped[track] || [];
          if (items.length === 0) return null;
          const Icon = meta.icon;
          return (
            <section key={track} data-testid={`drafts-section-${track}`}>
              <div className="flex items-center gap-2 mb-3">
                <Icon className="h-4 w-4 text-primary" />
                <h2 className="font-heading text-lg font-semibold text-primary">{meta.label}</h2>
                <Badge variant="secondary" className="bg-slate-100 text-slate-700 text-[10px]">{items.length}</Badge>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {items.map((d) => (
                  <Card key={d.id} className="p-4 border-slate-200 hover:border-primary/40 transition" data-testid={`draft-card-${d.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <Badge className="bg-primary text-white text-[10px] mb-1.5">{meta.badge}</Badge>
                        <div className="text-sm font-semibold text-primary truncate" title={d.note}>
                          {d.note || "Untitled draft"}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Last saved: {new Date(d.last_saved_at).toLocaleString()}
                          {d.save_count > 1 && (<> · {d.save_count} saves</>)}
                        </div>
                        {d.accreditation_type && (
                          <Badge variant="outline" className="mt-2 capitalize text-[10px] bg-slate-50">
                            {d.accreditation_type}
                          </Badge>
                        )}
                        {d.credential_type && (
                          <Badge variant="outline" className="mt-2 ml-1 text-[10px] bg-slate-50">
                            {d.credential_type}
                          </Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-red-600 -mt-1"
                        onClick={() => remove(d.id)}
                        data-testid={`draft-delete-${d.id}`}
                        title="Delete draft"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Link
                      to={`${meta.resume}?draft=${d.id}`}
                      data-testid={`draft-resume-${d.id}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      Resume <ArrowRight className="h-3 w-3" />
                    </Link>
                  </Card>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
